import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import dayjs from "dayjs";
import { ACTIVITY_TYPES } from "../../utils/activityTypes";
import { DEFAULT_TIME_ZONE } from "../../utils/timeZones";
import useDebouncedValue from "../../hooks/useDebouncedValue";
import ActivityLogsFilterBar from "./ActivityLogsFilterBar";
import ActivityLogsTable from "./ActivityLogsTable";

const TIME_ZONE_STORAGE_KEY = "activityLogsTimeZone";

const ACTIVITY_OPTIONS_FOR_DROPDOWN = ACTIVITY_TYPES.map(({ value, label }) => ({
  value: String(value),
  label,
}));

// Helper: find latest available date (<= today) from list
const getLatestAvailableDate = (dates) => {
  if (!dates || !dates.length) return null;

  const todayStr = dayjs().format("YYYY-MM-DD");
  const validDates = dates.filter((d) => d <= todayStr);

  const baseList = validDates.length ? validDates : dates;
  const sorted = baseList.toSorted(); // ascending
  const lastDateStr = sorted[sorted.length - 1];

  return dayjs(lastDateStr);
};

// Shared table+filters shell for the 4 activity-log pages (SuperAdmin/HealthCare
// x User/Device). Each page only differs in which id route param it reads,
// which pair of API calls it makes, and where "Back" navigates — everything
// else (columns, filters, pagination, calendar) is identical across all 4.
const ActivityLogsPage = ({
  idParam,
  idTransform = (v) => v,
  fetchLogs,
  fetchCalendar,
  backRoute,
  defaultFromTab,
}) => {
  const params = useParams();
  const id = idTransform(params[idParam]);
  const navigate = useNavigate();
  const location = useLocation();
  const fromTab = location.state?.fromTab || defaultFromTab;

  const [activities, setActivities] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [totalRows, setTotalRows] = useState(0);

  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearchQuery = useDebouncedValue(searchQuery, 500);
  const [activityType, setActivityType] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [fetchError, setFetchError] = useState("");
  const [calendarOpen, setCalendarOpen] = useState(false);

  // selectedDate will be set from calendar API (latest date with data)
  const [selectedDate, setSelectedDate] = useState(null);
  // default baseline date for filters
  const [defaultDate, setDefaultDate] = useState(null);

  const [hasActiveFilters, setHasActiveFilters] = useState(false);
  const [highlightedDates, setHighlightedDates] = useState([]);
  const [timeZone, setTimeZone] = useState(
    () => localStorage.getItem(TIME_ZONE_STORAGE_KEY) || DEFAULT_TIME_ZONE,
  );

  const handleTimeZoneChange = (value) => {
    setTimeZone(value);
    localStorage.setItem(TIME_ZONE_STORAGE_KEY, value);
  };

  const handleDateChange = (dates) => {
    const date = dates[0];
    if (!date) return;
    setSelectedDate(dayjs(date));
    setPage(0);
  };

  const fetchActivityLogs = useCallback(
    async (pageIndex, pageSize, searchString, actType, date) => {
      if (!date) return; // don't call API without a valid date

      setIsLoading(true);
      setFetchError("");

      const formattedDate = dayjs(date).format("YYYY-MM-DD");
      let selectedLabels = [];
      let activityLabelForPayload = null;

      if (Array.isArray(actType) && actType.length > 0) {
        selectedLabels = actType
          .map((val) => ACTIVITY_TYPES.find((t) => String(t.value) === String(val))?.label)
          .filter((lbl) => lbl && lbl !== "None");

        if (selectedLabels.length === 1) {
          activityLabelForPayload = selectedLabels[0];
        } else {
          activityLabelForPayload = null;
        }
      } else if (typeof actType === "number" || typeof actType === "string") {
        const selectedActivity = ACTIVITY_TYPES.find((t) => String(t.value) === String(actType));
        if (selectedActivity && selectedActivity.label !== "None") {
          selectedLabels = [selectedActivity.label];
          activityLabelForPayload = selectedActivity.label;
        }
      }

      const payload = {
        pageIndex: pageIndex,
        pageSize: selectedLabels.length > 1 ? 500 : pageSize,
        searchString: searchString ?? "",
        selectedDate: formattedDate,
        activityType: activityLabelForPayload,
        orderBy: 0,
      };

      try {
        const res = await fetchLogs(payload, id);
        const { data, statusCode } = res;
        if (statusCode === 200 && Array.isArray(data)) {
          let filteredData = data;
          if (selectedLabels.length > 1) {
            filteredData = data.filter((item) => selectedLabels.includes(item.activityType));
          }
          setActivities(filteredData);
          setTotalRows(res.totalRecords || filteredData.length);
        } else {
          setActivities([]);
          setTotalRows(0);
          setFetchError(res.message || "Failed to load activity logs");
        }
      } catch (error) {
        setActivities([]);
        setTotalRows(0);
        setFetchError(error?.message || "Failed to load activity logs");
      }
      setIsLoading(false);
    },
    [id, fetchLogs],
  );

  // Fetch data when dependencies change (only when selectedDate exists).
  const prevSearchQueryRef = useRef(debouncedSearchQuery);
  useEffect(() => {
    if (!(id && selectedDate)) return;

    const searchChanged = prevSearchQueryRef.current !== debouncedSearchQuery;
    prevSearchQueryRef.current = debouncedSearchQuery;
    const pageToFetch = searchChanged ? 0 : page;

    if (searchChanged && page !== 0) {
      setPage(0);
    }
    fetchActivityLogs(pageToFetch, rowsPerPage, debouncedSearchQuery, activityType, selectedDate);
  }, [fetchActivityLogs, id, page, rowsPerPage, debouncedSearchQuery, activityType, selectedDate]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const handleBack = () => {
    navigate(backRoute, { state: { activeTab: fromTab } });
  };

  const fetchCalendarData = useCallback(async () => {
    const hadInitialDate = Boolean(selectedDate);
    try {
      const response = await fetchCalendar(id);
      if (response.statusCode === 200) {
        const dates = response?.data?.map((item) => dayjs(item.date).format("YYYY-MM-DD"));
        setHighlightedDates(dates);

        if (!hadInitialDate && dates.length) {
          const latest = getLatestAvailableDate(dates);
          if (latest) {
            setSelectedDate(latest);
            setDefaultDate(latest);
            return;
          }
        }
      } else {
        setHighlightedDates([]);
      }
    } catch (error) {
      setHighlightedDates([]);
    }
    if (!hadInitialDate) setIsLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, fetchCalendar]);

  // Load calendar data on mount / id change
  useEffect(() => {
    if (id) {
      fetchCalendarData();
    } else {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Active filters (search, activity type, date ≠ default)
  useEffect(() => {
    const dateChanged =
      defaultDate && selectedDate
        ? selectedDate.format("YYYY-MM-DD") !== defaultDate.format("YYYY-MM-DD")
        : false;

    const isFilterActive =
      searchQuery !== "" ||
      (Array.isArray(activityType) ? activityType.length > 0 : activityType !== 0) ||
      dateChanged;

    setHasActiveFilters(isFilterActive);
  }, [searchQuery, activityType, selectedDate, defaultDate]);

  // Clear filters: reset to defaultDate (last date with data)
  const handleClearFilters = () => {
    setSearchQuery("");
    setActivityType([]);
    if (defaultDate) {
      setSelectedDate(defaultDate);
    }
    setPage(0);
  };

  const handleRemoveActivityTag = (valToRemove) => {
    setActivityType((prev) =>
      Array.isArray(prev) ? prev.filter((v) => String(v) !== String(valToRemove)) : []
    );
    setPage(0);
  };

  const handleRefresh = () => {
    fetchActivityLogs(page, rowsPerPage, debouncedSearchQuery, activityType, selectedDate);
  };

  return (
    <div className="logs_container">
      <ActivityLogsFilterBar
        searchQuery={searchQuery}
        onSearchChange={handleSearchChange}
        onRefresh={handleRefresh}
        onBack={handleBack}
        hasActiveFilters={hasActiveFilters}
        onClearFilters={handleClearFilters}
        activityType={activityType}
        onActivityTypeChange={(value) => setActivityType(value)}
        onRemoveActivityTag={handleRemoveActivityTag}
        activityOptions={ACTIVITY_OPTIONS_FOR_DROPDOWN}
        calendarOpen={calendarOpen}
        onCalendarOpen={() => setCalendarOpen(true)}
        onCalendarClose={() => setCalendarOpen(false)}
        selectedDate={selectedDate}
        onDateChange={handleDateChange}
        highlightedDates={highlightedDates}
        timeZone={timeZone}
        onTimeZoneChange={handleTimeZoneChange}
      />

      <ActivityLogsTable
        activities={activities}
        isLoading={isLoading}
        fetchError={fetchError}
        onRetry={handleRefresh}
        totalRows={totalRows}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        timeZone={timeZone}
      />
    </div>
  );
};

export default ActivityLogsPage;
